import type { App } from 'vue';
import { StepType, type PublicWidgetOptions } from './types/widget';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';

import { defineCustomElement } from 'vue';
import * as components from './components';
import utils from './utils';
import { config, applyConfig as applyAdminConfig } from './utils/config';
import { request } from './utils/request';
import { API_PATHS, isSource } from './types/api';
import { useSubmission } from './components/form/util/composition';
import {
  allowedGroups,
  areCookiesEnabled,
  CATEGORY_ANALYTICS,
  getClarity,
  loadAnalytics,
  loadClarity,
  loadOneTrust,
  loadOpenReplay,
  onConsentReady,
} from 'src/utils/analytics';
import { useStatistics } from './composables/statistics';
import * as Sentry from '@sentry/vue';
import packageJson from '../package.json';

export { default as ClaimsWidget } from './components/claims-widget/ClaimsWidget.ce.vue';

export let initialConfig: {
  apiKeyOrClientId: string;
  optionsOrToken: PublicWidgetOptions | string;
  callbacks?: Pick<PublicWidgetOptions, 'onSuccess' | 'onChange' | 'onError'>;
};

function initializeBrowserOnlyFeatures(app?: App) {
  if (typeof window !== 'undefined') {
    if (window?.customElements && !window.customElements.get('claims-widget')) {
      const WidgetCustomElement = defineCustomElement(components.ClaimsWidget, {
        configureApp(vueApp) {
          if (env.APP_SENTRY_DSN) {
            Sentry.init({
              app: vueApp,
              dsn: env.APP_SENTRY_DSN,
              release: packageJson.version,
              integrations: [Sentry.replayIntegration()],
              tracesSampleRate: Number(env.APP_SENTRY_SAMPLE_RATE),
              sampleRate: Number(env.APP_SENTRY_SAMPLE_RATE),
              environment: env.APP_ENV,
              replaysSessionSampleRate: Number(env.APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE),
              replaysOnErrorSampleRate: Number(env.APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE),
            });
          }
        },
      });
      window.customElements.define('claims-widget', WidgetCustomElement);
    }

    if (app && env.APP_SENTRY_DSN && !window.customElements?.get('claims-widget')) {
      Sentry.init({
        app,
        dsn: env.APP_SENTRY_DSN,
        release: packageJson.version,
        integrations: [Sentry.replayIntegration()],
        tracesSampleRate: Number(env.APP_SENTRY_SAMPLE_RATE),
        sampleRate: Number(env.APP_SENTRY_SAMPLE_RATE),
        environment: env.APP_ENV,
        replaysSessionSampleRate: Number(env.APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE),
        replaysOnErrorSampleRate: Number(env.APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE),
      });
    }
  }
}

// --- Dayjs Extensions ---
dayjs.extend(isBetween);
dayjs.extend(timezone);

// --- Vue Plugin Install Function ---
/**
 * Initializes the widget configuration and browser-specific features when used with app.use().
 * Does NOT register the ClaimsWidget component globally. Import ClaimsWidget directly.
 */
export function install(app: App) {
  initializeBrowserOnlyFeatures(app);
}

// --- Session Tracking (Keep internal checks if needed) ---
async function setupSessionTracking(
  userId: string,
  query?: { session?: string; reference?: string },
  isReinit?: boolean
) {
  // Ensure request or subsequent logic is SSR safe if called outside browser context
  const [progressData, sessionError] = await request.get(API_PATHS.session, { params: userId, query });
  if (sessionError?.message) {
    config.value.sessionData = {
      createdAt: '',
      userId: userId,
      id: '',
      currentSession: {},
      submission: undefined,
      statistics: { preSubmissionAccessCount: 0, postSubmissionAccessCount: 0 },
      userStatistics: undefined,
    };
    if (typeof window !== 'undefined' && (query?.reference || query?.session)) {
      console.error('This session is invalid, please try later!');
      config.value.error = 'This session is invalid, please try later!';
      return;
    }
    console.error('An unknown error happened while setting up session tracking, please try later!');
    config.value.error = 'An unknown error happened while setting up session tracking, please try later!';
    return;
  }
  if (progressData) {
    config.value.sessionData = progressData;
    if (isSource(progressData.currentSession)) {
      if (progressData.currentSession?.missingProperties) {
        config.value.insurance = progressData.currentSession?.missingProperties?.selectedInsurance;
        config.value.driver = progressData.currentSession?.missingProperties?.selectedUser;
      }
      if (progressData.currentSession?.context) {
        config.value.context = progressData.currentSession?.context;
      }
    }
    if (!isReinit) {
      config.value.reference = progressData?.submissionReference;
    } else {
      config.value.reference = undefined;
    }
    if (typeof window !== 'undefined' && progressData?.id) {
      const clarity = getClarity();
      if (clarity) {
        clarity('set', 'sessionId', progressData.id);
      }
    }
    (async () => {
      if (typeof window !== 'undefined' && progressData?.id) {
        const { getAll } = useStatistics();
        const userStats = await getAll();
        request.put(API_PATHS.statistics, progressData?.id, {
          userStatistics: userStats,
        });
      }
    })();
    return;
  }
  config.value.sessionData = {
    createdAt: '',
    userId: userId,
    id: '',
    currentSession: {},
    submission: undefined,
    statistics: { preSubmissionAccessCount: 0, postSubmissionAccessCount: 0 },
    userStatistics: undefined,
  };
}
const listeners: Array<{ off: () => void }> = [];

export async function _load(
  apiKeyOrClientId: string,
  optionsOrToken: PublicWidgetOptions | string,
  callbacks?: Pick<PublicWidgetOptions, 'onSuccess' | 'onChange' | 'onError'>,
  isReinit?: boolean
) {
  try {
    initialConfig = {
      apiKeyOrClientId,
      optionsOrToken,
      callbacks,
    };
    config.value.error = '';
    if (!optionsOrToken) {
      throw 'You must pass the required options (clientId, contact) or token to the widget';
    }
    config.value.isConfigLoading = true;
    if (!apiKeyOrClientId) {
      throw 'You must provide an API key or Client ID';
    }

    let userId: string | undefined;
    let sessionQuery: { session?: string; reference?: string } | undefined;

    if (typeof optionsOrToken === 'string') {
      config.value.clientId = apiKeyOrClientId;
      const [result, error] = await request.validate({ token: optionsOrToken });
      if (error?.message) throw error.message;
      if (!result) throw 'Unknown error validating token, please try again later';

      applyAdminConfig(result);
      userId = config.value.currentUserId;
      sessionQuery = { session: result.session, reference: result.reference };

      if (typeof window !== 'undefined') {
        listeners.forEach((listener) => listener.off());
        const { onError, onSuccess, onChange } = useSubmission();
        if (callbacks?.onSuccess) listeners[0] = onSuccess(callbacks.onSuccess);
        if (callbacks?.onError) listeners[1] = onError(callbacks.onError);
        if (callbacks?.onChange) listeners[2] = onChange(callbacks.onChange);
      }
    } else {
      if (!optionsOrToken?.clientId) {
        throw 'You must pass the required options (clientId) to the widget';
      }
      config.value.apiKey = apiKeyOrClientId;
      config.value.clientId = optionsOrToken.clientId;
      const [result, error] = await request.get(API_PATHS.config);
      if (error?.message) throw error.message;
      if (!result) throw 'Unknown error fetching config, please try again later';

      // @ts-expect-error test mode is developer only feature
      if (optionsOrToken?.testMode) {
        applyAdminConfig({
          ...result,
          ...optionsOrToken,
          privacyPolicyUrl: optionsOrToken.privacyPolicyUrl || result.privacyPolicyUrl,
        });
      } else {
        applyAdminConfig(
          { ...result, privacyPolicyUrl: optionsOrToken.privacyPolicyUrl || result.privacyPolicyUrl },
          optionsOrToken
        );
      }

      if (config.value.steps.type === StepType.CLAIMS) {
        const hasContactInfo = !!config.value?.contact?.phone || !!config.value?.contact?.email;
        const hasApiContactInfo =
          !!config.value?.settings?.contactInformation?.default &&
          Object.keys(config.value?.settings?.contactInformation?.default)?.length > 0;
        if (!hasApiContactInfo && !hasContactInfo) {
          throw 'You must pass the required options (contact) to the widget';
        }
      }
      userId = config.value.currentUserId;
      sessionQuery = { session: optionsOrToken.session, ...(isReinit ? {} : { reference: optionsOrToken.reference }) };
    }
    if (areCookiesEnabled()) {
      loadOneTrust();
      onConsentReady(() => {
        const groups = allowedGroups();
        if (groups.includes(CATEGORY_ANALYTICS)) {
          loadAnalytics();
          loadOpenReplay();
          loadClarity();
        }
      });
    }

    if (typeof window !== 'undefined' && userId && config.value.sessionTracking) {
      await setupSessionTracking(userId, sessionQuery, isReinit);
    }
  } catch (error) {
    console.error(`Axitech Widget load error:`, error);
    config.value.error = error as string;
    config.value.apiKey = ''; // Clear API key on error
  } finally {
    config.value.isConfigLoading = false;
  }
}

/**
 * Loads the client configuration using the API key for authentication, any options you provide here will overwrite the default client config.
 * @param apiKey - The API key for authentication and authorization.
 * @param options - Configuration options for the widget.
 * @example
 *
 * ```ts
 * interface PublicWidgetOptions {
 *   clientId: string; // your client id
 *   contact: {
 *     phone: string; // your contact number
 *     email: string; // your contact email
 *     link?: string; // any link for more information
 *   }
 *   ... // all other optional parameter
 * }
 * ```
 *
 * @returns A Promise that resolves when the widget is fully loaded and ready for interaction.
 */
export async function load(apiKey: string, options: PublicWidgetOptions): Promise<void>;
/**
 * Loads the client configuration using the client ID, any token you provide here will be decoded using your API key.
 * @param clientId - The client ID associated with the widget configuration.
 * @param token - JSON web token (JWT) encoding the configuration settings (options: PublicWidgetOptions), which will overwrite the default client config.
 * @example
 *
 * ```ts
 * interface PublicWidgetOptions {
 *   clientId: string; // your client id
 *   contact: {
 *     phone: string; // your contact number
 *     email: string; // your contact email
 *     link?: string; // any link for more information
 *   }
 *   ... // all other optional parameter
 * }
 * ```
 *
 * @param callbacks - Optional object containing callback functions
 * @example
 *
 * ```ts
 * callbacks = {
 *   onSuccess: (data) => {
 *     console.log('Successful submission', data);
 *   },
 *   onError: console.error,
 *   ... // all other optional callbacks the widget provides
 * }
 * ```
 *
 * @returns A Promise that resolves when the widget is fully loaded and ready for interaction.
 */
export async function load(
  clientId: string,
  token: string,
  callbacks?: Pick<PublicWidgetOptions, 'onSuccess' | 'onChange' | 'onError'>
): Promise<void>;
export async function load(
  apiKeyOrClientId: string,
  optionsOrToken: PublicWidgetOptions | string,
  callbacks?: Pick<PublicWidgetOptions, 'onSuccess' | 'onChange' | 'onError'>
): Promise<void> {
  initializeBrowserOnlyFeatures();
  await _load(apiKeyOrClientId, optionsOrToken, callbacks);
}

/** Constants of the widget */
export * from './constants';

/** Use applyConfig to apply any changes at runtime if you have not had the chance to change them at initialization */
export const applyConfig = utils.applyPublicConfig;

/** Export the updateWidgetCookieConsent function for external use */
export { updateWidgetCookieConsent } from './utils/cookie-consent';

export * from './types/widget';
